import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type QuillInstance = {
  on: (event: string, handler: () => void) => void;
  root: { innerHTML: string };
  clipboard: {
    convert: (value: string | { html: string }) => unknown;
    dangerouslyPasteHTML?: (html: string, source?: string) => void;
  };
  setContents: (contents: unknown, source?: string) => void;
  setText: (text: string) => void;
  getSemanticHTML?: () => string;
  enable: (enable?: boolean) => void;
  getModule: (name: string) => {
    addHandler: (name: string, handler: () => void) => void;
  };
  insertEmbed: (index: number, type: string, value: string, source?: string) => void;
  getSelection: (focus?: boolean) => { index: number; length: number } | null;
  setSelection: (index: number, length?: number, source?: string) => void;
};

@Component({
  selector: 'doc-rich-text-editor',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'doc-rich-text-editor' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  @ViewChild('editorHost', { static: true })
  editorHost!: ElementRef<HTMLDivElement>;

  @ViewChild('toolbarHost', { static: true })
  toolbarHost!: ElementRef<HTMLDivElement>;

  @Input() placeholder = '';

  @Input()
  set disabled(value: boolean) {
    this.setDisabledState(!!value);
  }

  private quill: QuillInstance | null = null;
  private quillVersion = '';
  private pendingValue = '';
  private isDisabled = false;
  private isSettingContent = false;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngAfterViewInit(): void {
    void this.initEditor();
  }

  ngOnDestroy(): void {
    this.quill = null;
  }

  writeValue(value: string | null): void {
    const nextValue = value ?? '';
    this.pendingValue = nextValue;

    if (!this.quill) {
      return;
    }

    this.setEditorHtml(nextValue);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.quill?.enable(!isDisabled);
  }

  getHtmlValue(): string {
    return this.getEditorHtml();
  }

  syncValue(): string {
    const html = this.getEditorHtml();
    this.pendingValue = html;
    this.onChange(html);
    return html;
  }

  private async initEditor(): Promise<void> {
    const quillModule = await import('quill');
    const Quill = quillModule.default as {
      new (
        element: HTMLElement,
        options: Record<string, unknown>
      ): QuillInstance;
      version?: string;
      import?: (path: string) => { whitelist?: string[] };
    };

    this.quillVersion = Quill.version ?? '';

    // Ensure size whitelist exists for Quill 2 size picker
    try {
      const Size = Quill.import?.('attributors/style/size') as
        | { whitelist?: string[] }
        | undefined;
      if (Size) {
        Size.whitelist = ['small', 'large', 'huge'];
      }
    } catch {
      // ignore if attributors path differs by Quill version
    }

    this.quill = new Quill(this.editorHost.nativeElement, {
      theme: 'snow',
      placeholder: this.placeholder,
      modules: {
        toolbar: {
          container: this.toolbarHost.nativeElement,
          handlers: {
            image: () => this.pickAndInsertMedia('image'),
            video: () => this.pickAndInsertMedia('video'),
          },
        },
      },
    });

    if (this.isDisabled) {
      this.quill.enable(false);
    }

    this.quill.on('text-change', () => {
      if (this.isSettingContent) {
        return;
      }
      this.onChange(this.getEditorHtml());
      this.onTouched();
    });

    if (this.pendingValue) {
      this.setEditorHtml(this.pendingValue);
    }
  }

  private pickAndInsertMedia(type: 'image' | 'video'): void {
    if (!this.quill) {
      return;
    }

    const accept = type === 'image' ? 'image/*' : 'video/*';
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', accept);
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      document.body.removeChild(input);
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const url = typeof reader.result === 'string' ? reader.result : '';
        if (!url || !this.quill) {
          return;
        }
        const range = this.quill.getSelection(true) || { index: 0, length: 0 };
        this.quill.insertEmbed(range.index, type, url, 'user');
        this.quill.setSelection(range.index + 1, 0, 'user');
        this.onChange(this.getEditorHtml());
        this.onTouched();
      };
      reader.readAsDataURL(file);
    });

    input.click();
  }

  private setEditorHtml(value: string): void {
    if (!this.quill) {
      return;
    }

    this.isSettingContent = true;
    try {
      if (!value?.trim()) {
        this.quill.setText('');
        return;
      }

      if (this.quill.clipboard.dangerouslyPasteHTML) {
        this.quill.setText('');
        this.quill.clipboard.dangerouslyPasteHTML(value, 'silent');
        return;
      }

      const converted = this.quill.clipboard.convert(
        this.quillVersion.startsWith('2') ? { html: value } : value
      );
      this.quill.setContents(converted, 'silent');
    } finally {
      this.isSettingContent = false;
    }
  }

  private getEditorHtml(): string {
    if (!this.quill) {
      return '';
    }

    const html = this.quill.getSemanticHTML
      ? this.quill.getSemanticHTML()
      : this.quill.root.innerHTML;

    if (!html || html === '<p><br></p>') {
      return '';
    }

    return html;
  }
}
